import React, { useState, useEffect } from 'react';
import Layout from '../components/Layout';
import DashboardSkeleton from '../components/Dashboard/DashboardSkeleton';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { useNavigate } from 'react-router-dom';
import api from '../utils/api';
import { useAuth } from '../contexts/AuthContext';
import { useTenant } from '../contexts/TenantContext';
import { formatCurrency, formatDate, getStatusColor } from '../utils/helpers';
import { PlusCircle, FileText, CheckCircle, DollarSign, TrendingUp, Users } from 'lucide-react';
import { toast } from 'sonner';

const Dashboard = () => {
  const navigate = useNavigate();
  const { user, isB2BAgent, isB2BAdmin, isAdmin, showCostBreakdown } = useAuth();
  const { buildPath } = useTenant();
  const [stats, setStats] = useState(null);
  const [recentQuotes, setRecentQuotes] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchDashboardData();
  }, []);

  const fetchDashboardData = async () => {
    try {
      // Use different endpoints based on role
      const promises = [api.get('/quotations?limit=10')];
      
      // Only internal users have access to admin dashboard stats
      if (!isB2BAgent) {
        promises.unshift(api.get('/admin/dashboard'));
      }
      
      const results = await Promise.all(promises);
      
      if (!isB2BAgent) {
        setStats(results[0].data);
        setRecentQuotes(results[1].data.slice(0, 10));
      } else {
        // For B2B agents, calculate stats from quotations
        const quotes = results[0].data;
        const thisMonth = new Date().getMonth();
        const thisYear = new Date().getFullYear();
        const quotesThisMonth = quotes.filter(q => {
          const d = new Date(q.created_at);
          return d.getMonth() === thisMonth && d.getFullYear() === thisYear;
        });
        
        setStats({
          quotes_this_month: quotesThisMonth.length,
          total_quotes: quotes.length,
          confirmed_quotes: quotes.filter(q => q.status === 'CONFIRMED').length,
          draft_quotes: quotes.filter(q => q.status === 'DRAFT').length
        });
        setRecentQuotes(quotes.slice(0, 10));
      }
    } catch (error) {
      console.error('Dashboard error:', error);
      toast.error('Failed to load dashboard data');
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return <DashboardSkeleton />;
  }

  // Get display name based on role
  const displayName = isB2BAgent 
    ? (user?.company_name || user?.branding_name || user?.full_name)
    : 'User';

  return (
    <Layout>
      <div className="space-y-6 sm:space-y-8" data-testid="dashboard">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center gap-4">
          <div>
            <h1 className="text-2xl sm:text-3xl font-brand font-bold text-navy">Dashboard</h1>
            <p className="text-sm sm:text-base text-navy-mid font-ui mt-1">
              Welcome back, {displayName}!
              {isB2BAgent && (
                <span className="ml-2 px-2 py-0.5 text-xs bg-cyan-pale text-cyan rounded-full font-medium">
                  {user?.role === 'B2B_AGENT_ADMIN' ? 'Partner Admin' : 'Partner'}
                </span>
              )}
            </p>
          </div>
          <div className="flex gap-2">
            {isB2BAdmin && (
              <Button
                variant="outline"
                onClick={() => navigate(buildPath('/sub-users'))}
                data-testid="manage-team-btn"
                className="border-[rgba(0,200,232,0.15)] text-navy hover:bg-light-bg"
              >
                <Users className="w-4 h-4 mr-2" />
                Team
              </Button>
            )}
            <Button
              onClick={() => navigate(buildPath('/quotations/create'))}
              className="bg-cyan hover:bg-cyan-l text-navy font-medium"
              data-testid="create-new-quote-button"
            >
              <PlusCircle className="w-4 h-4 mr-2" />
              Create New Quote
            </Button>
          </div>
        </div>

        {/* Stats Cards */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 sm:gap-6">
          <Card className="border-[rgba(0,200,232,0.15)] hover:border-cyan/30 transition-colors">
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-navy-mid font-ui">Quotes This Month</p>
                  <p className="text-3xl font-brand font-bold text-navy mt-2" data-testid="quotes-this-month">
                    {stats?.quotes_this_month || 0}
                  </p>
                </div>
                <div className="w-12 h-12 bg-cyan-pale rounded-xl flex items-center justify-center">
                  <FileText className="w-6 h-6 text-cyan" />
                </div>
              </div>
            </CardContent>
          </Card>

          <Card className="border-[rgba(0,200,232,0.15)] hover:border-cyan/30 transition-colors">
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-navy-mid font-ui">Confirmed</p>
                  <p className="text-3xl font-brand font-bold text-success mt-2" data-testid="confirmed-count">
                    {stats?.confirmed_count || 0}
                  </p>
                </div>
                <div className="w-12 h-12 bg-success/15 rounded-xl flex items-center justify-center">
                  <CheckCircle className="w-6 h-6 text-success" />
                </div>
              </div>
            </CardContent>
          </Card>

          <Card className="border-[rgba(0,200,232,0.15)] hover:border-cyan/30 transition-colors">
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-navy-mid font-ui">Draft</p>
                  <p className="text-3xl font-brand font-bold text-navy-mid mt-2">
                    {stats?.draft_count || 0}
                  </p>
                </div>
                <div className="w-12 h-12 bg-gray-100 rounded-full flex items-center justify-center">
                  <FileText className="w-6 h-6 text-gray-600" />
                </div>
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-gray-600">Total Revenue</p>
                  <p className="text-2xl font-bold text-green-600 mt-2" data-testid="total-revenue">
                    {formatCurrency(stats?.total_revenue || 0)}
                  </p>
                </div>
                <div className="w-12 h-12 bg-green-100 rounded-full flex items-center justify-center">
                  <DollarSign className="w-6 h-6 text-green-600" />
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Recent Quotations */}
        <Card>
          <CardHeader>
            <CardTitle>Recent Quotations</CardTitle>
          </CardHeader>
          <CardContent>
            {recentQuotes.length === 0 ? (
              <div className="text-center py-12">
                <FileText className="w-12 h-12 text-gray-400 mx-auto mb-4" />
                <p className="text-gray-600 mb-4">No quotations yet</p>
                <Button onClick={() => navigate(buildPath('/quotations/create'))}>
                  Create Your First Quote
                </Button>
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full" data-testid="recent-quotations-table">
                  <thead>
                    <tr className="border-b border-gray-200">
                      <th className="text-left py-3 px-4 text-sm font-semibold text-gray-700">Quote Ref</th>
                      <th className="text-left py-3 px-4 text-sm font-semibold text-gray-700">Guest Name</th>
                      <th className="text-left py-3 px-4 text-sm font-semibold text-gray-700">Date</th>
                      <th className="text-left py-3 px-4 text-sm font-semibold text-gray-700">Pax</th>
                      <th className="text-left py-3 px-4 text-sm font-semibold text-gray-700">Amount</th>
                      <th className="text-left py-3 px-4 text-sm font-semibold text-gray-700">Status</th>
                      <th className="text-left py-3 px-4 text-sm font-semibold text-gray-700">Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {recentQuotes.map((quote) => (
                      <tr key={quote.quotation_id} className="border-b border-gray-100 hover:bg-gray-50">
                        <td className="py-3 px-4 text-sm font-medium text-blue-600">
                          {quote.quote_reference}
                        </td>
                        <td className="py-3 px-4 text-sm text-gray-900">{quote.guest_name}</td>
                        <td className="py-3 px-4 text-sm text-gray-600">
                          {formatDate(quote.tour_start_date)}
                        </td>
                        <td className="py-3 px-4 text-sm text-gray-600">{quote.total_pax}</td>
                        <td className="py-3 px-4 text-sm font-semibold text-gray-900">
                          {formatCurrency(quote.net_quoted_price)}
                        </td>
                        <td className="py-3 px-4">
                          <span className={`px-2 py-1 rounded-full text-xs font-medium ${getStatusColor(quote.status)}`}>
                            {quote.status}
                          </span>
                        </td>
                        <td className="py-3 px-4">
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => navigate(buildPath(`/quotations/${quote.quotation_id}`))}
                          >
                            View
                          </Button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </Layout>
  );
};

export default Dashboard;